/*
 * Project: bellcloud
 * File: apps/frontend/src/connections/deviceFrameRingParser.ts
 * Description: SOF~CRC~EOF(optional LF) 프레임 추출 — Web Serial / BLE / 시뮬 공통.
 *   프레이밍/무결성(SOF/CRC/EOF)만 담당한다. 동기 어긋남은 1바이트씩 전진하며 복구하고,
 *   SOF 없이 버퍼가 과도하게 커지면 초기화한다. (스캔-포워드 등 방어 휴리스틱은 제거)
 */

import { RingBuffer } from '@/connections/ringBuffer';
import { calculateCRC16 } from '@packages/utils/device/crc16';

interface DeviceFrameRingParserOptions {
  /** `console` 로그 접두 (예: '[BLE]', '[Serial]') */
  logPrefix: string;
  bufferCapacity?: number;
}

export class DeviceFrameRingParser {
  private readonly ringBuffer: RingBuffer;
  private readonly logPrefix: string;
  private mode: 'single' | 'stream' = 'single';
  private lastBufferLength = 0;
  private lastBufferChangeAt = 0;

  constructor(options: DeviceFrameRingParserOptions) {
    this.logPrefix = options.logPrefix;
    this.ringBuffer = new RingBuffer(options.bufferCapacity ?? 8 * 1024 * 1024);
  }

  getMode(): 'single' | 'stream' {
    return this.mode;
  }

  setMode(m: 'single' | 'stream'): void {
    this.mode = m;
  }

  get lastBufferChangeTime(): number {
    return this.lastBufferChangeAt;
  }

  available(): number {
    return this.ringBuffer.available();
  }

  /** 내부 RingBuffer (용량/드롭 모니터링) */
  get internalRingBuffer(): RingBuffer {
    return this.ringBuffer;
  }

  pushBytes(data: Uint8Array, now: number = Date.now()): void {
    this.ringBuffer.pushBytes(data);
    const mergedSize = this.ringBuffer.available();
    if (mergedSize !== this.lastBufferLength) {
      this.lastBufferLength = mergedSize;
      this.lastBufferChangeAt = now;
    }
  }

  clear(): void {
    this.ringBuffer.clear();
    this.lastBufferLength = 0;
    this.lastBufferChangeAt = 0;
  }

  extractPacket(): Uint8Array | null {
    const buf = this.ringBuffer;
    const MAX_PAYLOAD_LEN = 4096;
    const p = this.logPrefix;

    while (true) {
      const sofIdx = buf.find(0x3c);
      if (sofIdx === -1) {
        // SOF 없이 버퍼가 과도하게 크면 동기 복구를 위해 초기화
        if (buf.available() > 50000) {
          console.warn(`${p} SOF 없이 버퍼가 큼 (${buf.available()} bytes), 초기화`);
          this.clear();
        }
        return null;
      }

      if (sofIdx > 0) {
        buf.discard(sofIdx);
        continue;
      }

      if (buf.available() < 8) {
        return null; // 헤더 대기
      }

      const payloadLen = (buf.get(6) || 0) + ((buf.get(7) || 0) << 8);

      if (payloadLen > MAX_PAYLOAD_LEN) {
        // 잘못된 길이 → 가짜 SOF로 보고 1바이트 전진
        buf.discard(1);
        continue;
      }

      const expectedPacketSize = 8 + payloadLen + 2 + 1;
      const expectedWithNewline = expectedPacketSize + 1;

      if (buf.available() < expectedPacketSize) {
        return null; // 전체 패킷 대기
      }

      let hasNewline = false;
      let expected = expectedPacketSize;
      if (buf.available() >= expectedWithNewline && buf.get(expectedWithNewline - 1) === 0x0a) {
        hasNewline = true;
        expected = expectedWithNewline;
      }

      const packet = buf.slice(0, expected);

      const crcStartIndex = 8 + payloadLen;
      const crcRead = packet[crcStartIndex] | (packet[crcStartIndex + 1] << 8);
      const crcCalc = calculateCRC16(Array.from(packet.slice(1, crcStartIndex)));

      if (crcRead !== crcCalc) {
        // CRC 불일치: 가짜 SOF로 보고 1바이트 전진하여 재동기
        buf.discard(1);
        continue;
      }

      const eofIndex = hasNewline ? expected - 2 : expected - 1;
      if (packet[eofIndex] !== 0x3e) {
        buf.discard(1);
        continue;
      }

      buf.discard(expected);

      let packetToReturn = new Uint8Array(packet);
      if (packetToReturn[packetToReturn.length - 1] !== 0x0a) {
        packetToReturn = new Uint8Array([...Array.from(packetToReturn), 0x0a]);
      }
      return packetToReturn;
    }
  }
}
